import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useApp } from './AppContext';
import { isRegisteredSession } from '../lib/authSession';
import {
  derivePendingCertificateActions,
  type PendingCertificateAction,
} from '../lib/pendingCertificateActions';
import { builderProfileService } from '../services/builderProfileService';

type PendingActionsContextValue = {
  pendingCertificates: PendingCertificateAction[];
  pendingCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
};

const PendingActionsContext = createContext<PendingActionsContextValue | undefined>(undefined);

export function PendingActionsProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn, user, userTier, sessionConfirmed } = useApp();
  const isRegistered = isRegisteredSession(user, userTier, isLoggedIn);
  const shouldFetch = sessionConfirmed && isRegistered;

  const [pendingCertificates, setPendingCertificates] = useState<PendingCertificateAction[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!shouldFetch) {
      setPendingCertificates([]);
      return;
    }

    setLoading(true);
    try {
      const profile = await builderProfileService.getMeProfile();
      setPendingCertificates(derivePendingCertificateActions(profile));
    } catch {
      setPendingCertificates([]);
    } finally {
      setLoading(false);
    }
  }, [shouldFetch]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!shouldFetch) return;
    const onFocus = () => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh, shouldFetch]);

  const value = useMemo(
    () => ({
      pendingCertificates,
      pendingCount: pendingCertificates.length,
      loading,
      refresh,
    }),
    [pendingCertificates, loading, refresh],
  );

  return (
    <PendingActionsContext.Provider value={value}>{children}</PendingActionsContext.Provider>
  );
}

export function usePendingActions(): PendingActionsContextValue {
  const ctx = useContext(PendingActionsContext);
  if (!ctx) {
    throw new Error('usePendingActions must be used within PendingActionsProvider');
  }
  return ctx;
}
